import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { IS_CONFIGURED, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, KeyRound, User, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const homeFor = (role) =>
    role === "admin" ? "/admin" : role === "fisioterapista" ? "/physio" : "/patient";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username.trim(), accessCode.trim());
      toast.success(`Benvenuto, ${user?.full_name || user?.username}`);
      navigate(homeFor(user?.role), { replace: true });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="s2m-gradient flex min-h-screen items-center justify-center p-4">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl shadow-xl lg:grid-cols-2">
        {/* Brand panel */}
        <div className="s2m-hero-gradient relative hidden flex-col justify-between p-10 text-white lg:flex">
          <div className="flex items-center gap-3">
            <img src="/logo-s2m.png" alt="Science2Movement" className="h-14 w-14 rounded-2xl bg-white/90 object-cover p-1" />
            <span className="text-lg font-semibold">Science2Movement</span>
          </div>
          <div>
            <h2 className="text-4xl font-semibold leading-tight text-white">Il movimento è scienza.</h2>
            <p className="mt-4 max-w-sm text-white/85">
              Programmi di riabilitazione, monitoraggio dell'aderenza e comunicazione diretta con il tuo team clinico.
            </p>
          </div>
          <div className="flex gap-6 text-sm text-white/80">
            <div><p className="text-2xl font-bold text-white">3</p>Ruoli dedicati</div>
            <div><p className="text-2xl font-bold text-white">100%</p>Responsive</div>
          </div>
        </div>

        {/* Form panel */}
        <div className="flex flex-col justify-center bg-white p-8 sm:p-12">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src="/logo-s2m.png" alt="Science2Movement" className="h-12 w-12 rounded-xl object-cover" />
            <span className="text-lg font-semibold">Science2Movement</span>
          </div>

          <h1 className="text-3xl font-semibold">Accedi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Inserisci username e codice di accesso.</p>

          {!IS_CONFIGURED && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="config-warning">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>URL del backend S2M non ancora configurato. Imposta <code>REACT_APP_S2M_API_URL</code> per collegarti ai dati reali.</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="username" value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder="es. mario.rossi" className="pl-9" autoComplete="username" required
                  data-testid="login-username-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="access_code">Codice di accesso</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="access_code" type="password" value={accessCode} onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="••••••" className="pl-9" autoComplete="current-password" required
                  data-testid="login-code-input"
                />
              </div>
            </div>

            {error && (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</p>
            )}

            <Button type="submit" className="h-11 w-full text-base" disabled={loading} data-testid="login-submit-button">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accesso...</> : "Entra"}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground" data-testid="support-note">
            Per supporto tecnico scrivere a{" "}
            <a href="mailto:science2movement@gmail.com" className="font-medium text-primary underline-offset-2 hover:underline">
              science2movement@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
